import { useState, useMemo } from "react";
import { Modal, Input, Switch, Tooltip, Skeleton, Empty, Segmented } from "antd";
import Swal from "sweetalert2";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, LayoutOutlined,
  SearchOutlined, CheckCircleOutlined, StopOutlined, StarOutlined, StarFilled, CodeOutlined,
} from "@ant-design/icons";
import { isSuperAdmin } from "../utils/role";
import {
  useEmailWidgets, useCreateEmailWidget, useUpdateEmailWidget,
  useSetDefaultEmailWidget, useToggleEmailWidget, useDeleteEmailWidget,
} from "../hooks/useEmailWidgets";
import WidgetFrame from "./WidgetFrame";
import VisualWidgetEditor from "./VisualWidgetEditor";
import { previewWidget, countContentSlots, SAMPLE_BODY, SAMPLE_SUBJECT } from "./widgetPreview";

/**
 * Email widgets (2026-10-27) — the company email designs internal users send custom mails in.
 *
 * A widget is a full email: its header and footer are fixed company details, and its one
 * {{content}} slot is where the message goes. {{email_subject}} shows the subject being sent;
 * {{customer_name}}, {{company_name}} and the other placeholders resolve per recipient.
 * Bulk Email's "Custom" uses these, and any template can pick one.
 */

const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const SLOT_ERROR = "Widget must contain exactly one {{content}} slot";

const thStyle = {
  padding: "14px 16px", textAlign: "left", fontSize: 12, fontWeight: 700,
  color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};
const tdStyle = { padding: "14px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const label = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };
const hint = { fontSize: 11.5, color: "#6b7280", marginTop: 5, lineHeight: 1.5 };
const errorText = { color: "#dc2626", fontSize: 12, marginTop: 5 };
const code = { fontFamily: "monospace", fontSize: 11.5, background: "#f5f3ff", color: "#5b48e8", borderRadius: 4, padding: "1px 5px" };

/* ── Add / Edit modal ─────────────────────────────────────────────────────── */
const WidgetFormModal = ({ widget, onClose }) => {
  const isEdit = Boolean(widget?.id);
  const [name, setName] = useState(widget?.name || "");
  const [html, setHtml] = useState(widget?.html || "");
  const [isDefault, setIsDefault] = useState(Number(widget?.is_default) === 1);
  const [active, setActive] = useState(widget ? Number(widget.status) === 1 : true);
  const [touched, setTouched] = useState(false);
  // Visual (edit the words on the page) or HTML (the code). A new, empty design starts in HTML.
  const [mode, setMode] = useState(widget?.html ? "visual" : "html");

  const createMutation = useCreateEmailWidget();
  const updateMutation = useUpdateEmailWidget();
  const saving = createMutation.isPending || updateMutation.isPending;

  const slotOk = countContentSlots(html) === 1;
  const nameError = touched && !name.trim() ? "Name is required" : "";
  const preview = useMemo(() => previewWidget(html, SAMPLE_BODY, SAMPLE_SUBJECT), [html]);

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim() || !slotOk) return;
    const payload = { name: name.trim(), html, is_default: isDefault ? 1 : 0, status: active ? 1 : 0 };
    const mutation = isEdit ? updateMutation : createMutation;
    mutation.mutate(isEdit ? { id: widget.id, ...payload } : payload, { onSuccess: onClose });
  };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>{isEdit ? "Edit widget" : "Add widget"}</span>}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Save"}
      okButtonProps={{ loading: saving, disabled: html !== "" && !slotOk }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={1080}
    >
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 20, paddingTop: 4 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={label}>Name <span style={{ color: "#dc2626" }}>*</span></label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
              placeholder="e.g. Entermark — default" status={nameError ? "error" : ""} />
            {nameError && <div style={errorText}>{nameError}</div>}
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <label style={{ ...label, marginBottom: 0 }}>Layout <span style={{ color: "#dc2626" }}>*</span></label>
              <Segmented size="small" value={mode} onChange={setMode}
                options={[
                  { value: "visual", label: "Visual", icon: <EditOutlined />, disabled: !html.trim() },
                  { value: "html", label: "HTML", icon: <CodeOutlined /> },
                ]} />
            </div>
            {mode === "visual" && html.trim() ? (
              <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden", maxHeight: 470, overflowY: "auto" }}>
                <VisualWidgetEditor html={html} onChange={setHtml} />
              </div>
            ) : (
              <Input.TextArea value={html} onChange={(e) => setHtml(e.target.value)} rows={18}
                placeholder="Paste the full email HTML here — header and footer with {{content}} where the message goes"
                status={html !== "" && !slotOk ? "error" : ""}
                style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.55 }} />
            )}
            {(html !== "" || touched) && !slotOk
              ? <div style={errorText}>{SLOT_ERROR}</div>
              : mode === "visual" ? (
                <div style={hint}>
                  Click any words — header, sign-off, footer — and type. The dashed boxes are the sender's: the
                  greeting (they can change it when sending) and the message. <span style={code}>{"{{customer_name}}"}</span> and
                  the other placeholders fill in per recipient.
                </div>
              ) : (
                <div style={hint}>
                  Put <span style={code}>{"{{content}}"}</span> where the message goes, <span style={code}>{"{{greeting}}"}</span> for an
                  opening line the sender can change, and <span style={code}>{"{{email_subject}}"}</span> where the subject should show.
                  <span style={code}>{"{{customer_name}}"}</span>, <span style={code}>{"{{company_logo}}"}</span> and the other
                  placeholders fill in per recipient.
                </div>
              )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Switch checked={isDefault} onChange={setIsDefault} />
              <span style={{ fontSize: 13, fontWeight: 600, color: isDefault ? "#5b48e8" : "#6b7280" }}>Default widget</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Switch checked={active} onChange={setActive} />
              <span style={{ fontSize: 13, fontWeight: 600, color: active ? "#16a34a" : "#6b7280" }}>{active ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>

        <div>
          <label style={label}>Live preview</label>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", maxHeight: 520, overflowY: "auto", background: "#eef0f7" }}>
            {html.trim()
              ? <WidgetFrame html={preview} title="Widget live preview" />
              : (
                <div style={{ height: 420, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, color: "#9ca3af", fontSize: 13 }}>
                  <LayoutOutlined style={{ fontSize: 30, color: "#d1d5db" }} />
                  <span>Paste the widget HTML to see it here</span>
                </div>
              )}
          </div>
          <div style={hint}>Sample message and subject shown — real mails carry what the sender writes.</div>
        </div>
      </div>
    </Modal>
  );
};

/* ── Page ─────────────────────────────────────────────────────────────────── */
const ManageEmailWidgets = () => {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);   // widget object, or {} to add

  const { data: widgets = [], isLoading, refetch, isFetching } = useEmailWidgets();
  const defaultMutation = useSetDefaultEmailWidget();
  const toggleMutation = useToggleEmailWidget();
  const deleteMutation = useDeleteEmailWidget();
  const mayManage = isSuperAdmin();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? widgets.filter((w) => w.name?.toLowerCase().includes(q)) : widgets;
  }, [widgets, search]);

  const handleToggle = (w) => {
    const isActive = Number(w.status) === 1;
    Swal.fire({
      title: isActive ? "Switch this widget off?" : "Switch this widget on?",
      html: isActive
        ? `<b>${escapeHtml(w.name)}</b> disappears from Bulk Email and the template pickers. Automated mails using it go out without the widget until it is back on.`
        : `<b>${escapeHtml(w.name)}</b> can be picked again.`,
      icon: isActive ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor: isActive ? "#dc2626" : "#16a34a",
      cancelButtonColor: "#6b7280",
      confirmButtonText: isActive ? "Yes, switch off" : "Yes, switch on",
      reverseButtons: true,
    }).then((r) => { if (r.isConfirmed) toggleMutation.mutate(w.id); });
  };

  const handleDelete = (w) => {
    Swal.fire({
      title: "Delete this widget?",
      html: `<b>${escapeHtml(w.name)}</b> will be removed for good.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    }).then((r) => { if (r.isConfirmed) deleteMutation.mutate(w.id); });
  };

  const iconBtn = (color, border, bg) => ({
    width: 32, height: 32, borderRadius: 8, border: `1px solid ${border}`,
    background: bg, color, cursor: "pointer", display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 13,
  });

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "28px 32px", marginBottom: 28, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, color: "white" }}>
                <LayoutOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>Email Widgets</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>Branded email layouts your team writes custom emails in</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Tooltip title="Refresh">
                <button onClick={() => refetch()} aria-label="Refresh" style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              {mayManage && (
                <button onClick={() => setEditing({})} style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 20px", borderRadius: 10, border: "none", background: "white", color: "#5b48e8", fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                  <PlusOutlined /> Add Widget
                </button>
              )}
            </div>
          </div>

          {/* Card */}
          <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f0f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…"
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} allowClear style={{ maxWidth: 340 }} />
              <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
                {filtered.length} {filtered.length === 1 ? "widget" : "widgets"}
              </span>
            </div>

            {isLoading ? (
              <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 5 }} /></div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "48px 20px" }}>
                <Empty description={search ? "No widget matches this search." : "No widgets yet — add your first email widget."} />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#fafbff" }}>
                    <tr>
                      <th style={thStyle}>Preview</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Templates</th>
                      <th style={thStyle}>Status</th>
                      {mayManage && <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((w) => {
                      const isActive = Number(w.status) === 1;
                      const isDefault = Number(w.is_default) === 1;
                      return (
                        <tr key={w.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={{ ...tdStyle, width: 220 }}>
                            <WidgetFrame html={previewWidget(w.html, SAMPLE_BODY, SAMPLE_SUBJECT)} title={`${w.name} preview`} scale={0.3} height={130} />
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>{w.name}</div>
                            {isDefault && (
                              <span style={{ background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                                Default
                              </span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, color: "#6b7280" }}>{`Used by ${Number(w.used_by) || 0}`}</td>
                          <td style={tdStyle}>
                            <span style={{ background: isActive ? "#f0fdf4" : "#f5f5f5", color: isActive ? "#16a34a" : "#6b7280", border: `1px solid ${isActive ? "#bbf7d0" : "#e5e7eb"}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          {mayManage && (
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <Tooltip title="Edit">
                                  <button onClick={() => setEditing(w)} aria-label={`Edit ${w.name}`} style={iconBtn("#5b48e8", "#ddd6fe", "#f5f3ff")}><EditOutlined /></button>
                                </Tooltip>
                                <Tooltip title={isDefault ? "Default widget" : "Make default"}>
                                  <button onClick={() => !isDefault && defaultMutation.mutate(w.id)} disabled={isDefault || defaultMutation.isPending}
                                    aria-label={`Make ${w.name} the default`} style={iconBtn("#b45309", "#fde68a", "#fffbeb")}>
                                    {isDefault ? <StarFilled /> : <StarOutlined />}
                                  </button>
                                </Tooltip>
                                <Tooltip title={isActive ? "Switch off" : "Switch on"}>
                                  <button onClick={() => handleToggle(w)} disabled={toggleMutation.isPending}
                                    style={iconBtn(isActive ? "#b45309" : "#16a34a", isActive ? "#fde68a" : "#bbf7d0", isActive ? "#fffbeb" : "#f0fdf4")}>
                                    {isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                                  </button>
                                </Tooltip>
                                <Tooltip title={Number(w.used_by) > 0 ? "Used by templates — move them to another widget first" : "Delete"}>
                                  <button onClick={() => handleDelete(w)} disabled={deleteMutation.isPending || Number(w.used_by) > 0}
                                    style={{ ...iconBtn("#dc2626", "#fecaca", "#fef2f2"), opacity: Number(w.used_by) > 0 ? 0.45 : 1 }}>
                                    <DeleteOutlined />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && <WidgetFormModal widget={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default ManageEmailWidgets;
